import { Outlet, createBrowserRouter, redirect, useParams, type RouteObject } from 'react-router';
import { PreserveSearchParams } from './routes/PreserveSearchParams';
import { ErrorRoute } from './routes/Error';
import { MainLayout } from './routes/MainLayout';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { Login } from './routes/Login';
import { userTopbarMenuPages } from './topBarMenuPages/userTopbarMenuPages';
import { isUUID } from './lib/uuid/isUUID';
import { useEffect } from 'react';
import { useZustandStore } from './appState/StoresProvider';
import { OneTimePassword } from './routes/OneTimePassword';
import { Spinner } from './components/Spinner/Spinner';
import styled, { useTheme } from 'styled-components';
import { useLoginStatus } from './appState/hooks/auth/useLoginStatus';
import { managerTopbarMenuPages } from './topBarMenuPages/managerTopbarMenuPages';
import { PrefetchLanguages } from './routes/PrefetchLanguages';
import type { LoginStatus } from './components/Login/useAuthStore';
import { getSessionTenantId } from './lib/storage/tenantSessionStorage';
import { useIsPermittedOrigin } from './appState/hooks/useOrigin';
import { resolveCallbackTenantId } from './lib/auth/resolveCallbackTenantId';

const searchParamsToPreserve: string[] = ['contentEntryFallbacks', 'code', 'state'];

function getAppRoutes({
  isPermittedOrigin,
  otpRequested,
  isManager,
  loginStatus,
}: {
  isPermittedOrigin: boolean;
  otpRequested: boolean;
  isManager?: boolean;
  loginStatus: LoginStatus;
}) {
  const urlParams = new URLSearchParams(window.location.search);
  const authCode = urlParams.get('code');
  const authState = urlParams.get('state');

  const basicAppRoutes: RouteObject[] = [
    {
      index: true,
      loader({ params: { tenantId } }) {
        const loaderResponse = isPermittedOrigin
          ? (testTenantId(tenantId) ?? redirect('overview'))
          : redirect('overview');
        return loaderResponse;
      },
    },
    {
      path: '',
      element: (
        <ProtectedRoute
          storePreviousLocationOnRedirect
          allowWhen={(loginStatus) => loginStatus === 'loggedIn' && isManager != null}
          redirectTo="login"
        />
      ),
      children: [
        {
          path: '',
          element: <PrefetchLanguages />,
          children: [
            {
              path: '',
              element: <MainLayout topBarMenuPages={userTopbarMenuPages} />,
              children: userTopbarMenuPages,
            },
            {
              path: 'manager',
              element: (
                <ProtectedRoute
                  allowWhen={() => isManager === true}
                  redirectTo="../overview"
                  storePreviousLocationOnRedirect
                />
              ),
              children: [
                {
                  index: true,
                  loader() {
                    return redirect('overview');
                  },
                },
                {
                  path: '',
                  element: <MainLayout topBarMenuPages={managerTopbarMenuPages} managerView />,
                  children: managerTopbarMenuPages,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: 'login',
      element: (
        <ProtectedRoute
          restoreStoredLocationOnRedirect
          allowWhen={(loginStatus) => loginStatus !== 'loggedIn' || isManager == null}
          fallbackRedirectTo="../overview"
        />
      ),
      children: [
        {
          path: '',
          element: <LoginRoute />,
          children: [
            {
              path: '',
              element:
                otpRequested || (loginStatus === 'loggedIn' && isManager == null) ? (
                  <OneTimePassword />
                ) : (
                  <Login authCode={authCode} authState={authState} />
                ),
            },
          ],
        },
      ],
    },
  ];
  return isPermittedOrigin ? withTenantId(basicAppRoutes) : basicAppRoutes;
}

export function getRouter({
  origin: _origin,
  otpRequested,
  isManager,
  loginStatus,
  isPermittedOrigin,
}: {
  origin: string;
  otpRequested: boolean;
  isManager?: boolean;
  loginStatus: LoginStatus;
  isPermittedOrigin: boolean; // Optional, defaults to true if not provided
}) {
  const appRoutes = getAppRoutes({ isPermittedOrigin, otpRequested, isManager, loginStatus });

  return createBrowserRouter([
    {
      path: '',
      element: <Initialization />,
      errorElement: <ErrorRoute />,

      children: [
        {
          path: '',
          element: <PreserveSearchParams paramKeys={searchParamsToPreserve} />,

          children: [
            ...appRoutes,
            {
              path: '',
              loader({ request, params: { tenantId } }) {
                const url = new URL(request.url);

                if (isPermittedOrigin) {
                  try {
                    testTenantId(tenantId);
                  } catch {
                    // A federated callback lands on the bare origin, so recover the tenant from
                    // the OAuth `state` parameter first and keep the search string (code + state)
                    // on the redirect (HRI2-16956).
                    const target = resolveCallbackTenantId({
                      code: url.searchParams.get('code'),
                      state: url.searchParams.get('state'),
                      storedTenantId: getSessionTenantId(),
                    });
                    return redirect(`/${target}${url.search}`);
                  }
                }
                throw jsonStatusResponse({ status: 404, statusText: 'Not found' });
              },
            },
          ],
        },
      ],
    },
  ]);
}

function withTenantId(appRoutes: RouteObject[]): RouteObject[] {
  return [
    {
      path: ':tenantId',
      loader({ params }) {
        return testTenantId(params.tenantId);
      },
      children: appRoutes,
    },
  ];
}
function testTenantId(tenantId: string | undefined) {
  if (!tenantId) {
    throw jsonStatusResponse({ status: 404, statusText: 'Not found. Invalid tenant code.' });
  }
  const isSegmentTenantCode = /^[A-Z0-9]{4}$/.exec(tenantId);
  const isSegmentTrainingDeliveryPath = /^[a-z0-9]{4,25}$/.exec(tenantId);
  const isSegmentUUID = isUUID(tenantId);

  if (!isSegmentTenantCode && !isSegmentTrainingDeliveryPath && !isSegmentUUID) {
    throw jsonStatusResponse({ status: 404, statusText: 'Not found. Invalid tenant code.' });
  }
  return null;
}

const Initialization: React.FC = () => {
  const { tenantId } = useParams();
  const init = useZustandStore('auth', (v) => v.init);
  const loginSettings = useZustandStore('auth', (v) => ('loginSettings' in v ? v.loginSettings : undefined));
  const settingsTenantId = useZustandStore('auth', (v) => v.tenantId);
  const loginStatus = useLoginStatus();
  const isPermittedOrigin = useIsPermittedOrigin();

  useEffect(() => {
    init(tenantId);
  }, [init, isPermittedOrigin, tenantId, loginStatus]);

  const theme = useTheme();

  // The auth store is persisted per tab, so on load it can still hold the settings of a tenant
  // visited earlier. Child effects run before this one's init(), so rendering those would let the
  // login screen initialise - and mint the OAuth state - for that other tenant (HRI2-16956).
  return loginSettings && settingsTenantId === tenantId ? (
    <Outlet />
  ) : (
    <SpinnerContainer>
      <Spinner size={'64px'} color={theme.colors.prototype.primary} />
    </SpinnerContainer>
  );
};

const LoginRoute: React.FC = () => {
  // why? because we need to clear the language when we go to the login page
  // to prevent RTL mode from being applied to the login page
  // (login pages are always english for now)
  const clearLanguage = useZustandStore('language', (v) => v.clearLanguage);
  useEffect(() => {
    clearLanguage();
  }, [clearLanguage]);
  return <Outlet />;
};

const SpinnerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

function jsonStatusResponse(payload: { status: number; statusText: string }) {
  return new Response(JSON.stringify(payload), {
    status: payload.status,
    statusText: payload.statusText,
    headers: { 'Content-Type': 'application/json' },
  });
}
